import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormRadio } from "@components/inputs/form/FormRadio/FormRadio";
import { useApiErrorUtils } from "@hooks/useApiErrorUtils";
import { useSubmitContactInformationMutation } from "@services/knowYourCustomer/mutations";
import { useContactInformationQuery } from "@services/knowYourCustomer/queries";
import {
  KycFormStep,
  type IContactInformation,
  type TKnowYourCustomerStepAction,
} from "@services/knowYourCustomer/types";
import { isNullOrUndefined } from "@utils/common";
import { KYCModalActionButtons } from "../KYCModalActionButtons/KYCModalActionButtons";
import { KYCModalPaging } from "../KYCModalPaging/KYCModalPaging";
import { ContactInformationAddressForm } from "./components/ContactInformationAddressForm/ContactInformationAddressForm";

interface IContactInformationFormProps {
  onChangeStep: (step: KycFormStep) => void;
}

export const ContactInformationForm: React.FC<IContactInformationFormProps> = ({
  onChangeStep,
}) => {
  const { t } = useTranslation();
  const { data: contactInformation } = useContactInformationQuery();

  const { mutateAsync: submitContactInformation, isPending } =
    useSubmitContactInformationMutation();

  const radioOptions = [
    {
      value: true,
      label: t(
        "KYC.modal.step2.form.fields.useSameAddressAsRegistration.options.yes",
      ),
    },
    {
      value: false,
      label: t(
        "KYC.modal.step2.form.fields.useSameAddressAsRegistration.options.no",
      ),
    },
  ];

  const { handleSubmit, control, resetField, setFocus, setValue, setError } =
    useForm<IContactInformation>({
      defaultValues: {
        registrationInformation: { email: "", phoneNumber: "" },
        useSameAddressAsRegistration:
          contactInformation?.useSameAddressAsRegistration,
      },
      values: contactInformation,
    });

  const [useSameAddressAsRegistrationValue] = useWatch({
    name: ["useSameAddressAsRegistration"],
    control,
  });

  const { setApiErrors } = useApiErrorUtils(setError);

  const submitContactInformationForm = async (
    data: IContactInformation,
    step?: TKnowYourCustomerStepAction,
  ) => {
    delete data.countryCodes;

    await submitContactInformation({ ...data, step })
      .then((response) => onChangeStep(response.currentForm))
      .catch(setApiErrors);
  };

  return (
    <div className="flex flex-col gap-4">
      <KYCModalPaging
        currentStep={KycFormStep.CONTACT_INFORMATION_FORM}
        onPreviousButtonClick={handleSubmit((data) =>
          submitContactInformationForm(data, "previous"),
        )}
        onNextButtonClick={handleSubmit((data) =>
          submitContactInformationForm(data),
        )}
        isLoading={isPending}
      />
      <p className="text-gray-900 p-0 text-lg font-semibold">
        {t("KYC.modal.step2.title")}
      </p>
      <form
        onSubmit={handleSubmit((data) => submitContactInformationForm(data))}
        className="flex flex-col gap-3"
      >
        {contactInformation?.countryCodes && (
          <ContactInformationAddressForm
            formFieldPrefix="registrationInformation"
            control={control}
            availableCountries={contactInformation.countryCodes}
            resetField={resetField}
            setFocus={setFocus}
            setValue={setValue}
          />
        )}
        <p className="text-gray-900 p-0 text-lg font-semibold">
          {t("KYC.modal.step2.form.fields.useSameAddressAsRegistration.title")}
        </p>
        {contactInformation && (
          <FormRadio
            name="useSameAddressAsRegistration"
            options={radioOptions}
            control={control}
            defaultValue={contactInformation.useSameAddressAsRegistration}
            className="mb-2 flex-row gap-0 [&>*]:w-1/2"
          />
        )}
        {!isNullOrUndefined(useSameAddressAsRegistrationValue) &&
          !useSameAddressAsRegistrationValue &&
          contactInformation?.countryCodes && (
            <ContactInformationAddressForm
              formFieldPrefix="placeOfResidence"
              control={control}
              availableCountries={contactInformation.countryCodes}
              resetField={resetField}
              setFocus={setFocus}
              setValue={setValue}
            />
          )}
      </form>
      <KYCModalActionButtons
        onSubmit={handleSubmit((data) => submitContactInformationForm(data))}
        onCancel={handleSubmit((data) =>
          submitContactInformationForm(data, "close"),
        )}
        isLoading={isPending}
      />
    </div>
  );
};
